import React from 'react'
import { Tooltip } from 'antd'
import { PauseCircleIcon, PlayCircleIcon, PencilIcon, PlusCircleIcon } from '@heroicons/react/24/outline'
import ActionButton from '../../../components/lists/Table/shared/ActionButton'
import { TeaserStatus, CompanyType } from '../../../__generated__/graphql'

// Helper function to format currency with consistent styling
const formatCurrency = (value: any) => {
  if (value === null || value === undefined) return '-'
  return (
    <span>
      <span className="text-xs text-gray-500">DKK </span>
      {parseFloat(value).toFixed(2)}
    </span>
  )
}

// Column options for teaser table - company type specific
export const getTeaserColumnOptions = (companyType: string) => {
  const baseOptions = [
    { label: "Teaser", value: "name", disabled: true },
    { label: "Impressions", value: "tdImpressions", disabled: true },
    { label: "Clicks", value: "tdClicks", disabled: true },
    { label: "Reads", value: "reads", disabled: true },
    { label: "Thumbnail", value: "tdImage" },
    { label: "Type", value: "tdType" },
    { label: "Clickouts", value: "clickOuts" },
    { label: "Read Time", value: "avgReadtime" },
    { label: "Conversions", value: "conversions" },
    { label: "CTR", value: "ctr" },
    { label: "Reads to impressions", value: "imps_reads" },
    { label: "Click Out Rate", value: "cotr" },
  ]

  return companyType === CompanyType.Publisher 
    ? [...baseOptions, { label: "Revenue", value: "publisherRevenue" }, { label: "eCPM", value: "eCpm" }]
    : [...baseOptions, { label: "Total Cost", value: "totalCost" }, { label: "eCPM", value: "eCpm" }, { label: "CPA", value: "cpa" }]
}


// Column options for website table - company type specific
export const getWebsiteColumnOptions = (companyType: string) => {
  const baseOptions = [
    { label: "Website/Teaser", value: "name", disabled: true },
    { label: "Impressions", value: "tdImpressions", disabled: true },
    { label: "Clicks", value: "tdClicks", disabled: true },
    { label: "Reads", value: "reads", disabled: true },
    { label: "Clickouts", value: "clickOuts" },
    { label: "Read Time", value: "avgReadtime" },
    { label: "Conversions", value: "conversions" },
    { label: "CTR", value: "ctr" },
    { label: "Reads to impressions", value: "imps_reads" },
    { label: "Click Out Rate", value: "cotr" },
  ]

  return companyType === CompanyType.Publisher 
    ? [...baseOptions, { label: "Revenue", value: "publisherRevenue" }, { label: "eCPM", value: "eCpm" }]
    : [...baseOptions, { label: "Total Cost", value: "totalCost" }, { label: "eCPM", value: "eCpm" }, { label: "CPA", value: "cpa" }]
}


// Website columns definition
export const createWebsiteColumns = () => [
  {
    title: 'Website/Teaser',
    dataIndex: 'name',
    key: 'name',
    fixed: 'left' as const,
    render: (text: any) => {
      if (!text) return '-'
      const isTruncated = text.length > 30
      return (
        <Tooltip title={isTruncated ? text : null}>
          {isTruncated ? `${text.substring(0, 30)}...` : text}
        </Tooltip>
      )
    },
  },
  {
    title: 'Impressions',
    dataIndex: 'tdImpressions',
    key: 'tdImpressions',
    sorter: (a: any, b: any) => a.tdImpressions - b.tdImpressions,
    render: (text: any) => text !== null && text !== undefined
      ? parseInt(text).toLocaleString()
      : '-',
  },
  {
    title: 'Clicks',
    dataIndex: 'tdClicks',
    key: 'tdClicks',
    sorter: (a: any, b: any) => a.tdClicks - b.tdClicks,
    render: (text: any) => text !== null && text !== undefined ? text.toLocaleString() : '-',
  },
  {
    title: 'Reads',
    dataIndex: 'reads',
    key: 'reads',
    sorter: (a: any, b: any) => a.reads - b.reads,
    render: (text: any) => text !== null && text !== undefined ? text.toLocaleString() : '-',
  },
  {
    title: 'Revenue',
    dataIndex: 'publisherRevenue',
    key: 'publisherRevenue',
    sorter: (a: any, b: any) => a.publisherRevenue - b.publisherRevenue,
    render: (text: any) => formatCurrency(text),
  },
  {
    title: 'Total Cost',
    dataIndex: 'totalCost',
    key: 'totalCost',
    sorter: (a: any, b: any) => a.totalCost - b.totalCost,
    render: (text: any) => formatCurrency(text),
  },
  {
    title: 'Clickouts',
    dataIndex: 'clickOuts',
    key: 'clickOuts',
    sorter: (a: any, b: any) => a.clickOuts - b.clickOuts,
    render: (text: any) => text !== null && text !== undefined ? text.toLocaleString() : '-',
  },
  {
    title: 'Read Time',
    dataIndex: 'avgReadtime',
    key: 'avgReadtime',
    sorter: (a: any, b: any) => a.avgReadtime - b.avgReadtime,
    render: (text: any) => text !== null && text !== undefined ? `${text}sec` : '-',
  },
  {
    title: 'Conversions',
    dataIndex: 'conversions',
    key: 'conversions',
    sorter: (a: any, b: any) => a.conversions - b.conversions,
    render: (text: any) => text !== null && text !== undefined ? text : '-',
  },
  {
    title: 'eCPM',
    dataIndex: 'eCpm',
    key: 'eCpm',
    sorter: (a: any, b: any) => a.eCpm - b.eCpm,
    render: (text: any) => formatCurrency(text),
  },
  {
    title: 'CTR',
    dataIndex: 'ctr',
    key: 'ctr',
    sorter: (a: any, b: any) => a.ctr - b.ctr,
    render: (text: any) => text !== null && text !== undefined ? `${text}%` : '-',
  },
  {
    title: 'CPA',
    dataIndex: 'cpa',
    key: 'cpa',
    sorter: (a: any, b: any) => a.cpa - b.cpa,
    render: (text: any) => formatCurrency(text),
  },
  {
    title: 'Reads to impressions',
    dataIndex: 'imps_reads',
    key: 'imps_reads',
    sorter: (a: any, b: any) => a.imps_reads - b.imps_reads,
    render: (text: any) => text !== null && text !== undefined ? `${text}‰` : '-',
  },
  {
    title: 'Click Out Rate',
    dataIndex: 'cotr',
    key: 'cotr',
    sorter: (a: any, b: any) => a.cotr - b.cotr,
    render: (text: any) => text !== null && text !== undefined ? `${text}%` : '-',
  }
]

// Teaser columns definition with actions
export const createTeaserColumns = (
  onCreateOrUpdate: (row?: any) => void,
  onUpdateStatus: (row: any, status: TeaserStatus) => void
) => [
  {
    title: 'Teaser',
    dataIndex: 'name',
    key: 'name',
    fixed: 'left' as const,
    render: (text: any) => {
      if (!text) return '-'
      const isTruncated = text.length > 30
      return (
        <Tooltip title={isTruncated ? text : null}>
          {isTruncated ? `${text.substring(0, 30)}...` : text}
        </Tooltip>
      )
    },
  },
  {
    title: 'Thumbnail',
    dataIndex: 'tdImage',
    key: 'tdImage',
    render: (tdImage: any) => (
      tdImage && tdImage !== 'N/A'
        ? <img
          src={`https://res.cloudinary.com/dzhcms4s6/image/upload/c_thumb,h_50/f_png/${tdImage}`}
          alt="Thumbnail"
          className="rounded border"
        />
        : '-'
    ),
  },
  {
    title: 'Type',
    dataIndex: 'tdType',
    key: 'tdType',
    render: (text: any) => text || '-',
  },
  {
    title: 'Impressions',
    dataIndex: 'tdImpressions',
    key: 'tdImpressions',
    sorter: (a: any, b: any) => a.tdImpressions - b.tdImpressions,
    defaultSortOrder: 'descend' as const,
    render: (text: any) => text !== null && text !== undefined
      ? parseInt(text).toLocaleString()
      : '-',
  },
  {
    title: 'Clicks',
    dataIndex: 'tdClicks',
    key: 'tdClicks',
    sorter: (a: any, b: any) => a.tdClicks - b.tdClicks,
    render: (text: any) => text !== null && text !== undefined ? text.toLocaleString() : '-',
  },
  {
    title: 'Reads',
    dataIndex: 'reads',
    key: 'reads',
    sorter: (a: any, b: any) => a.reads - b.reads,
    render: (text: any) => text !== null && text !== undefined ? text.toLocaleString() : '-',
  },
  {
    title: 'Revenue',
    dataIndex: 'publisherRevenue',
    key: 'publisherRevenue',
    sorter: (a: any, b: any) => a.publisherRevenue - b.publisherRevenue,
    render: (text: any) => formatCurrency(text),
  },
  {
    title: 'Total Cost',
    dataIndex: 'totalCost',
    key: 'totalCost',
    sorter: (a: any, b: any) => a.totalCost - b.totalCost,
    render: (text: any) => formatCurrency(text),
  },
  {
    title: 'Clickouts',
    dataIndex: 'clickOuts',
    key: 'clickOuts',
    sorter: (a: any, b: any) => a.clickOuts - b.clickOuts,
    render: (text: any) => text !== null && text !== undefined ? text.toLocaleString() : '-',
  },
  {
    title: 'Read Time',
    dataIndex: 'avgReadtime',
    key: 'avgReadtime',
    sorter: (a: any, b: any) => a.avgReadtime - b.avgReadtime,
    render: (text: any) => text !== null && text !== undefined ? `${text}sec` : '-',
  },
  {
    title: 'Conversions',
    dataIndex: 'conversions',
    key: 'conversions',
    sorter: (a: any, b: any) => a.conversions - b.conversions,
    render: (text: any) => text !== null && text !== undefined ? text : '-',
  },
  {
    title: 'eCPM',
    dataIndex: 'eCpm',
    key: 'eCpm',
    sorter: (a: any, b: any) => a.eCpm - b.eCpm,
    render: (text: any) => formatCurrency(text),
  },
  {
    title: 'CTR',
    dataIndex: 'ctr',
    key: 'ctr',
    sorter: (a: any, b: any) => a.ctr - b.ctr,
    render: (text: any) => text !== null && text !== undefined ? `${text}%` : '-',
  },
  {
    title: 'CPA',
    dataIndex: 'cpa',
    key: 'cpa',
    sorter: (a: any, b: any) => a.cpa - b.cpa,
    render: (text: any) => formatCurrency(text),
  },
  {
    title: 'Reads to impressions',
    dataIndex: 'imps_reads',
    key: 'imps_reads',
    sorter: (a: any, b: any) => a.imps_reads - b.imps_reads,
    render: (text: any) => text !== null && text !== undefined ? `${text}‰` : '-',
  },
  {
    title: 'Click Out Rate',
    dataIndex: 'cotr',
    key: 'cotr',
    sorter: (a: any, b: any) => a.cotr - b.cotr,
    render: (text: any) => text !== null && text !== undefined ? `${text}%` : '-',
  },
  {
    key: 'actions',
    fixed: 'right' as const,
    title: <ActionButton
      tooltip='Add teaser'
      row={{}}
      onClick={() => onCreateOrUpdate()}
      Icon={PlusCircleIcon}
    />,
    render: (_: any, row: any) => {
      if (row.trafficDriverId && !row.removed) {
        const isActive = row.status === TeaserStatus.Active
        return (
          <>
            <ActionButton
              tooltip="Edit teaser"
              row={row}
              onClick={() => onCreateOrUpdate(row)}
              Icon={PencilIcon}
            />
            <ActionButton
              tooltip={isActive ? "Pause teaser" : "Resume teaser"}
              row={row}
              onClick={() => onUpdateStatus(row, isActive ? TeaserStatus.Inactive : TeaserStatus.Active)}
              Icon={isActive ? PauseCircleIcon : PlayCircleIcon}
              className={isActive ? undefined : "text-green-800"}
            />
          </>
        )
      }
      return null
    }
  }
]

// Channel columns definition
export const channelColumns = (breakdownType: 'website' | 'source' = 'website') => [
  {
    title: breakdownType === 'website' ? 'Channel/Website' : 'Channel/Source',
    dataIndex: 'channel',
    key: 'channel',
    fixed: 'left' as const,
    render: (text: any, record: any) => {
      if (breakdownType === 'source') {
        return record.source || text
      }
      return record.website || text
    }
  },
  {
    title: 'Reads',
    dataIndex: 'reads',
    key: 'reads',
    sorter: (a: any, b: any) => a.reads - b.reads,
    defaultSortOrder: 'descend' as const,
    render: (text: any) => text?.toLocaleString() || '-'
  },
  {
    title: 'Readtime',
    dataIndex: 'avgReadtime',
    key: 'readtime',
    sorter: (a: any, b: any) => (a.avgReadtime || 0) - (b.avgReadtime || 0),
    render: (text: any) => text !== null && text !== undefined ? `${text}sec` : '-'
  },
  {
    title: 'Click Outs',
    dataIndex: 'clickOuts',
    key: 'clickOuts',
    sorter: (a: any, b: any) => a.clickOuts - b.clickOuts,
    render: (text: any) => text?.toLocaleString() || '-'
  },
  {
    title: 'Conversions',
    dataIndex: 'conversions',
    key: 'conversions',
    sorter: (a: any, b: any) => a.conversions - b.conversions,
    render: (text: any) => text?.toLocaleString() || '-'
  }
]
